import { createEffect, createSignal, onCleanup, onMount } from 'solid-js'

import Footer from './components/layout/Footer'
import Header from './components/layout/Header'
import AppShell from './components/layout/AppShell'
import SyncStatusBanner from './components/layout/SyncStatusBanner'
import TabNavigation from './components/navigation/TabNavigation'
import { useTabKeyboardNavigation } from './components/navigation/useTabKeyboardNavigation'
import { useTabSwipeNavigation } from './components/navigation/useTabSwipeNavigation'
import EditModal from './components/modals/EditModal'
import ExistingSyncKeyModal from './components/modals/ExistingSyncKeyModal'
import MeasurementEditModal from './components/modals/MeasurementEditModal'
import SleepEditModal from './components/modals/SleepEditModal'
import SyncKeyEditModal from './components/modals/SyncKeyEditModal'
import WeightEditModal from './components/modals/WeightEditModal'
import GlobalShareOverlay from './components/overlays/GlobalShareOverlay'
import AccountView from './pages/AccountView'
import CurrentFastView from './pages/CurrentFastView'
import HistoryView from './pages/HistoryView'
import HabitsView from './pages/HabitsView'
import MeasurementsView from './pages/MeasurementsView'
import { initializeFastingApp } from './legacyApp'
import { getLocale, refreshLocaleFromNavigator } from './i18n'
import {
   dispatchActiveTabChange,
   dispatchWideScreenViewportChange,
   dispatchWindowFocus,
   registerLegacyRuntimeHandlers,
   unregisterLegacyRuntimeHandlers,
} from './runtimeBridge'
import { accentTheme, applyAccentThemeToDocument } from './state/accentThemeStore'
import { applyColorThemeToDocument, colorTheme } from './state/colorThemeStore'
import {
   activeTab,
   isViewTransitioning,
   normalizeAppTab,
   setActiveTab,
   setTabMenuCollapsed,
   viewTransitionDirection,
} from './state/appUiStore'
import { useHabitAppBadge } from './utils/habitAppBadge'

/**
 * Root Solid component: wires locale, color/accent theme, habit badge, tab/URL sync, and the legacy runtime.
 *
 * @returns The app view tree.
 */
const App = () => {
   const [tabUrlSyncReady, setTabUrlSyncReady] = createSignal(false)
   const tabSwipeNavigation = useTabSwipeNavigation()
   useTabKeyboardNavigation()

   createEffect(() => {
      const locale = getLocale()
      if (typeof document !== 'undefined') {
         document.documentElement.lang = locale
      }
   })

   createEffect(() => {
      applyColorThemeToDocument(colorTheme())
      applyAccentThemeToDocument(accentTheme(), colorTheme())
   })

   useHabitAppBadge(dispatchWindowFocus)

   createEffect(() => {
      const tab = activeTab()
      if (typeof window === 'undefined') {
         return
      }
      if (!tabUrlSyncReady()) {
         return
      }

      const params = new URLSearchParams(window.location.search)
      params.set('tab', tab)
      const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`
      window.history.replaceState({ tab }, '', nextUrl)
   })

   onMount(() => {
      refreshLocaleFromNavigator()
      const initialParams = new URLSearchParams(window.location.search)
      setActiveTab(normalizeAppTab(initialParams.get('tab')), { animate: false })
      setTabUrlSyncReady(true)
      setTabMenuCollapsed(window.innerWidth >= 640)
      const runtime = initializeFastingApp()
      registerLegacyRuntimeHandlers(runtime)

      const wideScreenMedia = window.matchMedia('(min-width: 640px)')
      /**
       * Propagates wide-screen media-query changes to the runtime.
       *
       * @param event - The media query list or its change event.
       */
      const handleViewportChange = (event: MediaQueryListEvent | MediaQueryList) => {
         dispatchWideScreenViewportChange(event.matches)
      }
      handleViewportChange(wideScreenMedia)
      dispatchActiveTabChange(activeTab())
      if (typeof wideScreenMedia.addEventListener === 'function') {
         wideScreenMedia.addEventListener('change', handleViewportChange)
      } else if (typeof wideScreenMedia.addListener === 'function') {
         wideScreenMedia.addListener(handleViewportChange)
      }

      onCleanup(() => {
         unregisterLegacyRuntimeHandlers()
         if (typeof wideScreenMedia.removeEventListener === 'function') {
            wideScreenMedia.removeEventListener('change', handleViewportChange)
         } else if (typeof wideScreenMedia.removeListener === 'function') {
            wideScreenMedia.removeListener(handleViewportChange)
         }
      })
   })

   return (
      <>
         <AppShell>
            <Header />
            <SyncStatusBanner />
            <TabNavigation />
            <div
               id="view-stack"
               class="view-stack lg:col-span-2"
               data-transitioning={isViewTransitioning() ? 'true' : 'false'}
               data-transition-direction={viewTransitionDirection()}
               onPointerDown={tabSwipeNavigation.handlePointerDown}
               onPointerMove={tabSwipeNavigation.handlePointerMove}
               onPointerUp={tabSwipeNavigation.handlePointerUp}
               onPointerCancel={tabSwipeNavigation.resetSwipeGesture}
            >
               <CurrentFastView />
               <HistoryView />
               <HabitsView />
               <MeasurementsView />
               <AccountView />
            </div>
            <EditModal />
            <WeightEditModal />
            <MeasurementEditModal />
            <SleepEditModal />
            <ExistingSyncKeyModal />
            <SyncKeyEditModal />
         </AppShell>
         <Footer />
         <GlobalShareOverlay />
      </>
   )
}

export default App
